import { useCallback, useState } from 'react';
import { Loader2, Search, Wrench } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

import { useAgentToolsQuery, useLinkToolMutation } from '@/query/agent.query';

import { getToolSummary } from '@/lib/tool';

import useModalStore from '@/store/modal.store';

type SelectToolContentProps = {
  agentId: number;
};

export function SelectToolContent({ agentId }: SelectToolContentProps) {
  const [search, setSearch] = useState('');
  const [linkingToolId, setLinkingToolId] = useState<string | null>(null);

  const { data: availableTools = [], isLoading } = useAgentToolsQuery(agentId, false);

  const linkMutation = useLinkToolMutation(agentId, {
    onSuccess: () => useModalStore.getState().closeModal(),
  });

  const filteredTools = search
    ? availableTools.filter(tool => {
        return [tool.name, tool.description]
          .filter(Boolean)
          .some(value => value.toLowerCase().includes(search.toLowerCase()));
      })
    : availableTools;

  const handleLink = useCallback(
    (toolId: string) => {
      setLinkingToolId(toolId);
      linkMutation.mutate(toolId, {
        onSettled: () => setLinkingToolId(null),
      });
    },
    [linkMutation]
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add tool</DialogTitle>
        <DialogDescription>Select a tool to link to this agent.</DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search tools..."
            value={search}
            onChange={event => setSearch(event.target.value)}
            className="pl-9"
          />
        </div>

        <ScrollArea className="h-[280px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filteredTools.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center text-sm text-muted-foreground">
              <Wrench className="mb-3 h-8 w-8 opacity-40" />
              <p>
                {availableTools.length === 0
                  ? 'No tools are available to link yet.'
                  : 'No tools match your search.'}
              </p>
              <p className="mt-1 text-xs">
                {availableTools.length === 0
                  ? 'Create a tool from the tools page and then link it here.'
                  : 'Try a different search term.'}
              </p>
            </div>
          ) : (
            <div className="space-y-1 pr-3">
              {filteredTools.map(tool => {
                const isLinking = linkingToolId === tool.id && linkMutation.isPending;

                return (
                  <div
                    key={tool.id}
                    className="flex items-start gap-3 rounded-lg border border-transparent p-3 text-left transition-colors hover:bg-muted/50"
                  >
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      <Wrench className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{tool.name}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {getToolSummary(tool)}
                        </div>
                      </div>
                    </div>

                    <Button
                      type="button"
                      size="sm"
                      className="shrink-0"
                      onClick={() => handleLink(tool.id)}
                      disabled={linkMutation.isPending}
                    >
                      {isLinking && <Loader2 className="h-4 w-4 animate-spin" />}
                      {isLinking ? 'Linking...' : 'Link'}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => useModalStore.getState().closeModal()}>
          Cancel
        </Button>
      </DialogFooter>
    </>
  );
}
